import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  NgZone,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { DialogComponent } from "@components/layouts/dialog/dialog.component";
import { MatIconModule } from "@angular/material/icon";
import { MatTabChangeEvent, MatTabsModule } from "@angular/material/tabs";
import {
  AngularSignaturePadModule,
  NgSignaturePadOptions,
  SignaturePadComponent,
} from "@almothafar/angular-signature-pad";

import { MatButtonModule } from "@angular/material/button";
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";

function commentRequired(control: AbstractControl): ValidationErrors | null {
  const v = (control.value ?? "").toString().trim();
  return v ? null : { required: true };
}
import { Messages } from "@config/messages";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Constants } from "@config/constants";
import { UploadSignInitialsComponent } from "@components/upload-sign-initials/upload-sign-initials.component";
import { UploadSignRecentUploadsComponent } from "@components/upload-sign-recent-uploads/upload-sign-recent-uploads.component";
import { SignService } from "@services/sign.service";
import { LanguageService, SupportedLanguage } from "@services/language.service";
import { IGetSignerSignature } from "@modals/api.modal";
import {
  DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
  recolorSignatureImage,
  SELF_SIGN_SIGNATURE_COLORS,
} from "@utils/signature-color.util";

@Component({
  selector: "kzn-upload-sign",
  standalone: true,
  imports: [
    DialogComponent,
    MatTabsModule,
    MatIconModule,
    AngularSignaturePadModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    UploadSignInitialsComponent,
    UploadSignRecentUploadsComponent
],
  templateUrl: "./upload-sign.component.html",
  styleUrl: "./upload-sign.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class UploadSignComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(UploadSignInitialsComponent) uploadSignInitialsComponentRef!: UploadSignInitialsComponent;
  @ViewChild(UploadSignRecentUploadsComponent) uploadSignRecentUploadsComponentRef!: UploadSignRecentUploadsComponent;
  @ViewChild("uploadSignFileInput") uploadSignFileInputRef!: ElementRef;
  @ViewChild("commentCanvas") commentCanvas!: ElementRef;
  @ViewChild("signature")
  public signaturePad!: SignaturePadComponent;
  public signaturePadOptions: any = {
    minWidth: 2,
    canvasWidth: 0,
    canvasHeight: 180,
    penColor: DEFAULT_SELF_SIGN_SIGNATURE_COLOR,
  };
  public selectedImage: string | null = null;
  private _sourceImageBase64: string | null = null;
  public readonly selfSignSignatureColors = SELF_SIGN_SIGNATURE_COLORS;
  public selectedSignatureColor: string = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
  public signatureId: string = '';
  public Messages = Messages;
  public signComment = new FormControl("", Validators.maxLength(100));
  public docComment = new FormControl("", commentRequired);
  public isComment!: boolean;
  public isStamp = false;
  public signerName: string = '';
  public selfSignMode = false;
  public initialTabIndex = 0;
  private _isResponsiveContainer: boolean = false;
  public Constants = Constants;
  public showMaxFileSizeError: boolean = false;
  public showInvalidFileTypeError: boolean = false;
  public signService = inject(SignService);
  private _languageService = inject(LanguageService);
  private _cdr = inject(ChangeDetectorRef);
  private _ngZone = inject(NgZone);

  get dialogTitle(): string {
    if (this.isComment) return this.t('insertComment');
    if (this.isStamp) return this.t('insertStamp');
    return this.t('insertSignature');
  }

  get isInsertDisabled(): boolean {
    if (this.isComment) {
      return this.docComment.invalid;
    }
    return !(this.selectedImage ?? '').trim();
  }

  constructor(
    private dialogRef: MatDialogRef<UploadSignComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isComment: boolean;
      isStamp?: boolean;
      isResponsiveContainer: boolean;
      signerName: string;
      selfSignMode?: boolean;
    }
  ) {
    this.isComment = data.isComment ?? false;
    this.isStamp = data.isStamp ?? false;
    this.signerName = data.signerName ?? '';
    this.selfSignMode = data.selfSignMode ?? false;
    this._isResponsiveContainer = data.isResponsiveContainer ?? false;
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.resizeSignaturePad();
    this.applyPenColor(this.selectedSignatureColor);
  }

  ngOnDestroy(): void {}


  selectedTabChange(event: MatTabChangeEvent): void {
    const drawTabIndex = 1;

    if (!this.selfSignMode) {
      this.resetForm();
    } else {
      this.showMaxFileSizeError = false;
      this.showInvalidFileTypeError = false;
      if (this.uploadSignFileInputRef?.nativeElement) {
        this.uploadSignFileInputRef.nativeElement.value = '';
      }
    }

    if (event.index === drawTabIndex) {
      setTimeout(() => {
        this.resizeSignaturePad();
        this.signaturePad?.clear();
        this.selectedImage = null;
        this._cdr.markForCheck();
      });
    }
  }

  private syncSelectedImageFromPad(): void {
    if (!this.signaturePad || this.signaturePad.isEmpty()) {
      this._sourceImageBase64 = null;
      this.selectedImage = null;
      return;
    }
    this._sourceImageBase64 = this.signaturePad.toDataURL();
    void this.applySignatureColorToPreview();
  }

  private applyPenColor(color: string): void {
    this.signaturePadOptions = {
      ...this.signaturePadOptions,
      penColor: color,
    };
    if (this.signaturePad) {
      this.signaturePad.set('penColor', color);
    }
  }

  onSignatureColorChange(color: string): void {
    this.selectedSignatureColor = color;
    this.applyPenColor(color);
    void this.applySignatureColorToPreview();
  }

  private async applySignatureColorToPreview(): Promise<void> {
    const source = (this._sourceImageBase64 ?? '').trim();
    if (!source) {
      this.selectedImage = null;
      this._cdr.markForCheck();
      return;
    }

    if (!this.selfSignMode) {
      this.selectedImage = source;
      this._cdr.markForCheck();
      return;
    }

    try {
      this.selectedImage = await recolorSignatureImage(source, this.selectedSignatureColor);
    } catch {
      this.selectedImage = source;
    }
    this._cdr.markForCheck();
  }

  updateSelectedImage(image: string, signatureId?: string) {
    this._sourceImageBase64 = image;
    this.signatureId = signatureId ?? '';
    void this.applySignatureColorToPreview();
  }

  drawComplete(_event: MouseEvent | Touch) {
    //  console.log('Draw End', _event);

    this._ngZone.run(() => {
      this.syncSelectedImageFromPad();
      this._cdr.markForCheck();
    });
  }

  uploadSign(): void {
    this.uploadSignFileInputRef.nativeElement.click();
  }

  onFileInputChange(event: any) {
    const file = event.target?.files[0];

    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

      if (!allowedTypes.includes(file.type)) {
        this.showMaxFileSizeError = false;
        this.showInvalidFileTypeError = true;
        return;
      }

      this.showInvalidFileTypeError = false;
      const reader = new FileReader();
      if (file.size > Constants.MAX_SIGN_UPLOAD_FILE_SIZE_IN_KB) {
        this.showMaxFileSizeError = true;
        return;
      } else {
        this.showMaxFileSizeError = false;
      }

      reader.onloadend = async () => {
        this.updateSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }

  t(key: string): string {
    const k = (key || '').toString();
    return this._languageService.instant(k);
  }

  /** Sharp comment raster — matches field box size at 2× pixel density. */
  private buildCommentImageDataUrl(comment: string, fillColor: string): string {
    const canvasEl = this.commentCanvas?.nativeElement as HTMLCanvasElement | undefined;
    if (!canvasEl) return '';

    const scale = 2;
    const logicalW = Math.max(Constants.DEFAULT_SIGN_COMMENT_WIDTH, 300);
    const logicalH = Math.max(Constants.DEFAULT_SIGN_COMMENT_HEIGHT + 24, 84);
    const labelOverflow = 8;
    const totalH = logicalH + labelOverflow;
    const boxTop = labelOverflow;
    const padX = 12;
    const label =
      Constants.DEFAULT_COMMENT_PREFIX.replace('{ROLE}', 'Signer');
    const suffix = Constants.DEFAULT_COMMENT_SUFFIX;

    canvasEl.width = logicalW * scale;
    canvasEl.height = totalH * scale;

    const ctx = canvasEl.getContext('2d');
    if (!ctx) return '';

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(scale, scale);
    ctx.clearRect(0, 0, logicalW, totalH);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.38)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, boxTop + 0.5, logicalW - 1, logicalH - 1);

    ctx.font = '11px Arial, Helvetica, sans-serif';
    ctx.fillStyle = '#6b7280';
    const labelWidth = ctx.measureText(label).width;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fillRect(padX, boxTop - 7, labelWidth + 8, 14);
    ctx.fillStyle = '#6b7280';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, padX + 4, boxTop + 0.5);

    ctx.font = '14px Arial, Helvetica, sans-serif';
    ctx.fillStyle = fillColor;
    ctx.textBaseline = 'middle';
    const bodyY = boxTop + logicalH / 2;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.strokeText(comment, padX, bodyY, logicalW - padX * 2);
    ctx.fillText(comment, padX, bodyY, logicalW - padX * 2);

    const suffixMaxWidth = logicalW - padX * 2;
    let suffixFontSize = 8;
    while (suffixFontSize > 6) {
      ctx.font = `${suffixFontSize}px Arial, Helvetica, sans-serif`;
      if (ctx.measureText(suffix).width <= suffixMaxWidth) break;
      suffixFontSize -= 0.5;
    }
    ctx.fillStyle = '#1565c0';
    ctx.textBaseline = 'bottom';
    ctx.fillText(suffix, padX, boxTop + logicalH - 4, suffixMaxWidth);

    return canvasEl.toDataURL('image/png');
  }

  async insertImage() {
    let data = {};
    if (this.isComment) {
      this.docComment.markAsTouched();
      if (this.docComment.invalid) {
        return;
      }
      const comment = (this.docComment.value || "").toString().trim();
      const fillColor = this.selfSignMode
        ? this.selectedSignatureColor
        : DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
      const sourceImage = this.buildCommentImageDataUrl(comment, fillColor);
      if (!sourceImage) {
        return;
      }
      let imageBase64 = sourceImage;
      if (this.selfSignMode && this.selectedSignatureColor !== DEFAULT_SELF_SIGN_SIGNATURE_COLOR) {
        try {
          imageBase64 = await recolorSignatureImage(sourceImage, this.selectedSignatureColor);
        } catch {
          imageBase64 = sourceImage;
        }
      }
      data = {
        imageBase64,
        sourceImageBase64: sourceImage,
        signatureColor: this.selfSignMode ? this.selectedSignatureColor : undefined,
        comment,
        signComment: "",
        isCommentOnly: true,
        isResponsiveContainer: this._isResponsiveContainer
      };
    } else {
      if (!(this.selectedImage ?? '').trim()) {
        return;
      }
      data = {
        imageBase64: this.selectedImage,
        sourceImageBase64: this._sourceImageBase64 ?? this.selectedImage,
        signatureColor: this.selfSignMode ? this.selectedSignatureColor : undefined,
        signComment: this.signComment.value,
        isCommentOnly: false,
        signatureId: this.signatureId,
        isResponsiveContainer: this._isResponsiveContainer
      };
    }
    this.dialogRef.close(data);
  }

  cancelDialog(): void {
    this.dialogRef.close();
  }

  handleInitialImageChange(selectedInitialImage: string) {
    this.updateSelectedImage(selectedInitialImage);
  }

  handleRecentUploadImageChange(selectedRecentUploadImageData: {
    image: string,
    signatureId: string
  }) {
    this.updateSelectedImage(selectedRecentUploadImageData.image, selectedRecentUploadImageData.signatureId);
  }

  applyRecentSignature(signature: IGetSignerSignature): void {
    this.updateSelectedImage(signature.SignImagePath, signature.Id);
    this.uploadSignRecentUploadsComponentRef?.preselectSignature(signature);
  }

  resetForm() {
    this.signComment.reset();
    this.docComment.reset();
    this.selectedImage = null;
    this._sourceImageBase64 = null;
    this.selectedSignatureColor = DEFAULT_SELF_SIGN_SIGNATURE_COLOR;
    this.applyPenColor(this.selectedSignatureColor);
    this.signatureId = '';
    if (this.signaturePad) {
      this.signaturePad.clear();
    }
    const cCtx = this.commentCanvas?.nativeElement?.getContext("2d");
    if (cCtx) {
      cCtx.clearRect(0, 0, this.commentCanvas.nativeElement.width, this.commentCanvas.nativeElement.height);
    }
    this.showMaxFileSizeError = false;
    this.showInvalidFileTypeError = false;
    if (this.uploadSignFileInputRef?.nativeElement) this.uploadSignFileInputRef.nativeElement.value = '';
    this.uploadSignInitialsComponentRef?.resetInitials();
    this.uploadSignRecentUploadsComponentRef?.resetRecentUpload();
    this._cdr.markForCheck();
  }

  private resizeSignaturePad(): void {
    const container = document.getElementById("draw-tab-content");
    const width = container?.offsetWidth ? container.offsetWidth - 10 : 400;
    const height = Math.max(150, Math.floor(width * 0.35));
    this.signaturePadOptions = {
      ...this.signaturePadOptions,
      canvasWidth: width,
      canvasHeight: height,
    };
    if (this.signaturePad) {
      this.signaturePad.set('canvasWidth', width);
      this.signaturePad.set('canvasHeight', height);
      this.applyPenColor(this.selectedSignatureColor);
    }
  }

}
